import React, { FC } from 'react';
import PortalHeader from '../../assessment-portal/components/portal-header';
import {
  MOBILE_PRIMARY_CTA_CLASS,
  MOBILE_PRIMARY_CTA_ENABLED_CLASS,
} from '../../assessment-portal/components/primary-cta';

interface AssessmentIntroPageProps {
  showBack?: boolean;
  onBack?: () => void;
  onNext: () => void;
  dueDateLabel?: string;
}

const MOBILE_POINTS = [
  {
    key: 'length',
    tone: 'blue',
    title: '18 questions, about 6 minutes',
    body: 'One focused question at a time — nothing to write.',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="6" y="3" width="12" height="18" rx="2" />
        <path d="M9 3v2h6V3M9 11h6M9 15h4" />
      </svg>
    ),
  },
  {
    key: 'save',
    tone: 'green',
    title: 'Your answers save as you go',
    body: 'Kept on this device for 15 days — no account needed. We\'ll also text you a link so you can finish on any phone.',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M5 4h10l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
        <path d="M9 13l2 2 4-4" />
      </svg>
    ),
  },
  {
    key: 'honest',
    tone: 'amber',
    title: 'Answer honestly, not perfectly',
    body: 'There are no right or wrong answers. Your responses help your clinician understand your experience.',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 11.5a8.5 8.5 0 0 1-12 7.7L3 21l1.8-6A8.5 8.5 0 1 1 21 11.5Z" />
      </svg>
    ),
  },
] as const;

const AssessmentIntroPage: FC<AssessmentIntroPageProps> = ({
  showBack = false,
  onBack,
  onNext,
  dueDateLabel,
}) => {
  const duePrefix = dueDateLabel
    ? `Due before ${dueDateLabel}`
    : 'Due before your appointment';

  return (
    <div className="adhd-portal adhd-primer-page min-h-screen flex flex-col bg-page">
      <PortalHeader
        title="Before you start"
        subtitle="Pre-appointment questions"
        chip="6 min"
        chipTone="grey"
        progress={100}
        onBack={showBack && onBack ? onBack : undefined}
      />
      <main className="flex-1 flex items-start justify-center px-4 py-6 md:py-8">
        <div className="adhd-primer-card hidden md:block">
          <div className="adhd-primer-kicker">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <h1 className="adhd-primer-title">A few things before you start</h1>
          <p className="adhd-primer-sub">
            The questionnaire is the next step. Here&apos;s what to expect.
          </p>

          <div className="adhd-primer-points">
            <div className="adhd-primer-point">
              <span className="adhd-primer-pico">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </span>
              <div>
                <div className="adhd-primer-pt">About 45 minutes, 5 short sections</div>
                <div className="adhd-primer-pd">
                  It covers attention, hyperactivity, impulsivity and how symptoms
                  affect daily life — one focused question at a time.
                </div>
              </div>
            </div>

            <div className="adhd-primer-point">
              <span className="adhd-primer-pico">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 4h10l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
                  <path d="M9 13l2 2 4-4" />
                </svg>
              </span>
              <div>
                <div className="adhd-primer-pt">Save your progress and come back later</div>
                <div className="adhd-primer-pd">
                  Before you leave you&rsquo;ll be able to save your progress to this
                  device for 15 days — no account needed.
                </div>
              </div>
            </div>

            <div className="adhd-primer-point">
              <span className="adhd-primer-pico">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 11.5a8.5 8.5 0 0 1-12 7.7L3 21l1.8-6A8.5 8.5 0 1 1 21 11.5Z" />
                </svg>
              </span>
              <div>
                <div className="adhd-primer-pt">Answer honestly, not perfectly</div>
                <div className="adhd-primer-pd">
                  There are no right or wrong answers. Your responses help your
                  clinician understand your experience.
                </div>
              </div>
            </div>
          </div>

          <div className="adhd-primer-tip">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#0552F0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 18h6M10 21h4M12 3a6 6 0 0 1 4 10.5c-.6.6-1 1.4-1 2.2V16H9v-.3c0-.8-.4-1.6-1-2.2A6 6 0 0 1 12 3Z" />
            </svg>
            <div className="adhd-primer-tiptext">
              Find a quiet spot and settle in. Before you leave, you&rsquo;ll have the
              option to save your progress to this device — there&rsquo;s no pressure to rush.
            </div>
          </div>

          <div className="adhd-primer-actions">
            {showBack && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="adhd-primer-back"
              >
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M19 12H5M11 18l-6-6 6-6" />
                </svg>
                Back
              </button>
            )}
            <button
              type="button"
              onClick={onNext}
              className="adhd-primer-start"
            >
              Start questionnaire
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="adhd-primer-mobile md:hidden">
          <h1 className="adhd-primer-mobile-title">Before you start</h1>
          <p className="adhd-primer-mobile-lede">
            Questions about your focus, attention and daily life — one at a time.
          </p>

          <div className="adhd-primer-listcard">
            {MOBILE_POINTS.map((point) => (
              <div key={point.key} className="adhd-primer-listrow">
                <span className={`adhd-primer-pico adhd-primer-pico--${point.tone}`}>
                  {point.icon}
                </span>
                <div>
                  <div className="adhd-primer-pt">{point.title}</div>
                  <div className="adhd-primer-pd">{point.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="adhd-primer-due">
            <b>{duePrefix}</b>
            {' '}
            — there&apos;s no pressure to finish in one sitting.
          </div>
        </div>
      </main>

      <div className="portal-navrow-spacer md:hidden" aria-hidden />
      <div className="portal-navrow--sticky md:hidden">
        <button
          type="button"
          onClick={onNext}
          className={`${MOBILE_PRIMARY_CTA_CLASS} ${MOBILE_PRIMARY_CTA_ENABLED_CLASS}`}
        >
          Start questionnaire
        </button>
      </div>
    </div>
  );
};

export default AssessmentIntroPage;
